import { service } from '@maia/cart';
import { IconAngleRight, IconBolt, IconEnvelopeAlt, mapper } from '@maia/icons';
import {
  Button,
  ButtonLink,
  CartItem,
  Divider,
  FeaturedIcon,
  Typography,
} from '@maia/ui';
import { useMutation } from '@tanstack/react-query';
import {
  createFileRoute,
  Outlet,
  useMatches,
  useParams,
} from '@tanstack/react-router';
import { useMemo } from 'react';
import { match } from 'ts-pattern';
import { encodeQueryItem } from 'ufo';
import * as v from 'valibot';
import { getUpsellproductsOptions } from '../../../../features/add-insurance/upsell-products/queryOptions';
import {
  parseProductsFromQuery,
  replaceProductGuids,
} from '../../../../features/add-insurance/utils';
import { getCustomerOptions } from '../../../../features/customer/customer/queryOptions';
import { CustomerInformation } from '../../../../features/shared/customer/customer-information';
import { queryClient } from '../../../../main';
import { BillingUnitDto, BrokerDto } from '@maia/shared';

export const Route = createFileRoute(
  '/_authenticated/add-insurance/$customer/_layout',
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { customer: customerGuid },
  }) => {
    const upsellProducts = await queryClient.ensureQueryData(
      getUpsellproductsOptions({ customerGuid: customerGuid }),
    );

    const customer = await queryClient.ensureQueryData(
      getCustomerOptions(customerGuid),
    );
    return { upsellProducts, customer };
  },
  validateSearch: v.object({
    products: v.optional(v.string()),
  }),
});

function RouteComponent() {
  const { customer } = Route.useLoaderData();
  const matches = useMatches();
  const isSummaryPage =
    matches.find(
      (match) =>
        match.fullPath === '/add-insurance/$customer/cart/$cart/summary',
    )?.status === 'success';

  const isInsuranceSelectionPage =
    matches.find((match) => match.fullPath === '/add-insurance/$customer/')
      ?.status === 'success';

  return (
    <div className="layout-sidebar">
      <Outlet />

      <div className="flex flex-col area-page-sidebar">
        {match({ isSummaryPage, isInsuranceSelectionPage })
          .with({ isSummaryPage: true }, () => (
            <SubmitInsurance billingUnit={customer.billingUnit} />
          ))
          .with({ isInsuranceSelectionPage: true }, () => (
            <SubmitInsuranceSelection />
          ))
          .otherwise(() => (
            <CustomerInformation customer={customer} />
          ))}
      </div>
    </div>
  );
}

function SubmitInsuranceSelection() {
  const params = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const selectedProducts = useMemo(
    () => parseProductsFromQuery(search.products),
    [search],
  );
  const { customer, upsellProducts } = Route.useLoaderData();
  const products = useMemo(
    () =>
      upsellProducts.filter((product) =>
        selectedProducts.includes(
          product.riskPool
            ? `${product.guid}|${product.riskPool}`
            : product.guid,
        ),
      ),
    [upsellProducts, selectedProducts],
  );

  const selectionContainsObProduct = useMemo(() => {
    return products.some((product) => product.objectCode === 'OB');
  }, [selectedProducts, products]);

  const { mutate: getProducts, isPending: getProductsPending } = useMutation({
    mutationFn: (params: Parameters<typeof service.getProducts>[number]) =>
      service.getProducts(params),
    onSuccess: (response, request) => {
      const newGuids = replaceProductGuids(
        upsellProducts.map((upsellProducts) => {
          return upsellProducts.guid;
        }),
        response,
      ).filter(Boolean);
      const query = encodeQueryItem('guid', newGuids);
      return navigate({
        to: '/add-insurance/$customer/cart/$cart/product/$product',
        params: {
          cart: request.cartGuid,
          customer: customer.guid,
          product: newGuids[0],
        },
        search: {
          products: query,
        },
      });
    },
  });

  const { mutate: createCart, isPending: createCartPending } = useMutation({
    mutationFn: (
      params: Parameters<typeof service.createCart>[number]['body'],
    ) => {
      const me = queryClient.getQueryData<BrokerDto>(['getSelf']);

      return service.createCart({
        customerGuid: customer.guid,
        body: {
          selectedProducts: (params?.selectedProducts ?? []).map((p) => {
            const [guid, riskPool] = p.guid.split('|');
            return {
              objectReference: null,
              guid: guid,
              riskPool: riskPool ?? null,
            };
          }),
          salesChannel: me?.salesChannel ?? '',
        },
      });
    },
    onSuccess: ({ cart_guid }) =>
      getProducts({
        cartGuid: cart_guid,
        customerGuid: customer.guid,
      }),
  });

  return (
    <div className="flex flex-col gap-y-32 h-full">
      <CustomerInformation customer={customer} />

      <div>
        <Typography
          variant="body-s"
          weight="medium"
          className="inline-block mb-24"
        >
          Gekozen verzekeringen
        </Typography>
        <ul className="flex flex-col gap-y-24">
          {products.map((product) => (
            <li key={product.guid}>
              <div className="flex items-center gap-x-8">
                <FeaturedIcon
                  icon={mapper(product.icon)}
                  variant="light-square"
                  size="sm"
                />
                <Typography variant="body-xs" weight="semibold">
                  {product.name}
                </Typography>
              </div>
            </li>
          ))}
        </ul>
      </div>
      {selectionContainsObProduct ? (
        <ButtonLink
          intent="primary"
          iconRight={IconAngleRight}
          to="/add-insurance/$customer/ob"
          params={params}
          search={search}
          disabled={!selectedProducts.length}
          className="mt-auto w-full justify-center"
          size="large"
        >
          Volgende
        </ButtonLink>
      ) : (
        <Button
          className="mt-auto w-full justify-center"
          intent="primary"
          iconRight={IconAngleRight}
          onPress={() =>
            createCart({
              selectedProducts: (selectedProducts ?? []).map((guid) => ({
                guid,
              })),
            })
          }
          size="large"
          isDisabled={
            createCartPending || getProductsPending || !selectedProducts.length
          }
          isLoading={createCartPending || getProductsPending}
        >
          Volgende
        </Button>
      )}
    </div>
  );
}

function SubmitInsurance({ billingUnit }: { billingUnit: BillingUnitDto }) {
  const navigate = Route.useNavigate();
  const params = useParams({
    from: undefined,
    shouldThrow: false,
    strict: false,
  });

  const { mutate } = useMutation({
    mutationFn: (type: Parameters<typeof service.submitCart>[number]['type']) =>
      service.submitCart({
        cartGuid: params?.cart ?? '',
        customerGuid: params?.customer ?? '',
        type,
      }),
    onSuccess: (_, args) => {
      match(args)
        .with('order', () =>
          navigate({
            to: '/add-insurance/$customer/insurance-send',
            params: {
              customer: params?.customer,
            },
          }),
        )
        .with('quotation', () =>
          navigate({
            to: '/add-insurance/$customer/invoice-send',
            params: {
              customer: params?.customer,
            },
          }),
        )
        .with('special', () =>
          navigate({
            to: '/add-insurance/$customer/special-send',
            params: {
              customer: params?.customer,
            },
          }),
        )
        .exhaustive();
    },
  });

  return (
    <>
      <div className="flex flex-col gap-y-32 flex-1">
        <Typography
          variant="display6"
          weight="medium"
          className="text-primaryMain"
        >
          Afronding
        </Typography>

        <div className="flex flex-col gap-y-24">
          {/* TODO: Add once api works again */}
          <CartItem
            icon={IconBolt}
            name="Camperverzekering"
            specs={[
              { title: 'Title', description: 'description' },
              { title: 'Title', description: 'description' },
            ]}
            price={26}
            billingUnit={billingUnit}
          />
        </div>
      </div>

      <Divider className="-ml-32 -mr-32 my-12" />
      <ul className="space-y-12 mt-auto">
        <li>
          <Button
            intent="cta"
            size="large"
            className="w-full justify-center"
            onPress={() => mutate('order')}
          >
            Order plaatsen
          </Button>
        </li>
        {/* TODO: Add functionality when pressing this button */}
        <li>
          <Button
            intent="primary"
            iconLeft={IconEnvelopeAlt}
            size="large"
            className="w-full justify-center"
            onPress={() => mutate('quotation')}
          >
            Als offerte versturen
          </Button>
        </li>
        {/* TODO: Add functionality when pressing this button */}
        <li>
          <Button
            intent="tertiary"
            size="large"
            className="w-full justify-center"
            onPress={() => mutate('special')}
          >
            Maatwerk aanvragen
          </Button>
        </li>
      </ul>
    </>
  );
}
